import { defineStore } from 'pinia'
import { getCurrentInstance, getCurrentScope, onServerPrefetch, type ComponentInternalInstance, type EffectScope } from 'vue'
import { ExclusiveTo, ItemType } from '~/types/lineitem'
import type { AddProductApiPayload, AddProductPayload, Cart, CartProduct, CartSummary, CartApiResponse } from '~/types/cart'
import { useLocaleStore } from '~/stores/locale'
import { useAgentStore } from '~/stores/agent'
import { addCommonQueryParam } from '~/utils/general'
import { usePersistentStore } from '~/stores/persistent'
import { getIsValidDemoForCulture } from '~/utils'
import { hasApiErrorCode, toError, type StampinupError } from '~/utils/http/api-error'
import type { Wishlist } from '~/types/wishlist'
import { NotificationType } from '~/types/notifications'
import { pushAddToCart, pushRemoveFromCart } from '~/utils/gtm'
import { normalizeSlug } from '~/utils/shop'
import { useNavigationStore } from '~/stores/navigation'
import { useNotificationsStore } from '~/stores/notifications'
import { initCart, initCartSummary } from '~/utils/cart'
import type { QueryProductsAPIResponse } from '~/types/shop'
import { SHOP_ROOT } from '~/utils/categoryRoutes'
import { BONUS_RETURN_PATH } from '~/utils/cartRoutes'
import type { CartUpdateResult } from '~/service/cart'
import { isServer } from '~/utils/env'

const DEFAULT_MAX_LIMIT = 99
export enum URL_CART {
  CART = 'cart',
  COUPONCODE = '/couponcode/',
  COUPONCODES = '/couponcodes/',
  REMOVE_DEMOID = '/demo/',
  DEMOID = '/demoid/',
  WISHLIST = '/wishlist/',
  ADD_PROJECT = '/add-project'
}

export enum AddToCartSource {
  WISHLIST = 'wishlist',
  PRODUCT_LIST = 'product-list',
  FEATURED_PRODUCTS = 'featured-products',
  RECOMMENDED_PRODUCTS = 'recommended-products',
  EXPLORE_PRODUCTS = 'explore-products',
  DEMO_SEARCH = 'demo-search',
  PRODUCT_DETAIL = 'product-detail',
  RELATIONSHIP_COLLECTIONS = 'relationship-collections',
  RELATIONSHIP_BUNDLE = 'relationship-bundle',
  RELATIONSHIP_DIGITAL_CONTENT = 'relationship-digitalContent',
  RELATIONSHIP_COMPONENTS = 'relationship-components',
  RELATIONSHIP_DIRECT_COORDINATION = 'relationship-directCoordination',
  QUICK_ADD_BY_ID = 'quick-add-by-id',
  CMS_CONTENT = 'cms-content'
}

export type AddProductToCartResult = 'unsuccesful' | undefined

type CategoryRouteLike = {
  name?: unknown
  params?: {
    slug?: string | string[]
  }
}

export const getCategoryNameForCartRoute = (
  route: CategoryRouteLike | undefined,
  categoryBySlug: (slug: string) => { name?: string } | undefined
) => {
  if (route?.name !== 'shop-products-slug') {
    return undefined
  }
  const slug = route.params?.slug
  const slugParts = Array.isArray(slug) ? slug : slug ? [slug] : []
  return categoryBySlug(normalizeSlug([SHOP_ROOT, ...slugParts]))?.name
}

type CartStateResponse = {
  cart: Cart
  cartSummaryOverview: CartSummary
  errors?: StampinupError[]
  apiError?: {
    errors?: StampinupError[]
  }
}

const useCartStoreDefinition = defineStore('cart', () => {
  const { $appInsights, $cartService, $api: api } = useNuxtApp()
  const { displayMessage } = useErrorText()
  const { proxy: gtm } = useScriptGoogleTagManager()
  const router = useRouter()

  const agentStore = useAgentStore()
  const localeStore = useLocaleStore()
  const persistentStore = usePersistentStore()
  const navigationStore = useNavigationStore()
  // `isFailure` removed with AB#63254: CartFailureDialog rendered on it and nothing ever set
  // it, while the cart service raised rootStore.cartFailureDialog which nothing read. Both
  // ends are the root-store flag now.
  const cart = ref(initCart())
  const summary = ref<CartSummary>(initCartSummary())
  const errors = ref<StampinupError[]>([])

  // Helper function to update cart state from API response
  const updateCartState = (result: CartStateResponse) => {
    cart.value = result.cart
    summary.value = result.cartSummaryOverview
    errors.value = result.errors || []
  }

  const quickAddItem = async (payload: AddProductApiPayload) => {
    let data: CartStateResponse | undefined
    const query = { ...addCommonQueryParam(), quantity: payload.quantity }
    const body = agentStore.viewAsDate ? { ...payload, viewAsDate: agentStore.viewAsDate } : { ...payload }
    if (payload.itemType === ItemType.NORMAL) {
      data = await api<CartStateResponse>(`cart/${payload.productId}`, { query, method: 'post', body })
    }
    if (!data) { return undefined }
    errors.value = data.errors || data.apiError?.errors || []
    cart.value = data.cart
    summary.value = data.cartSummaryOverview
    return data.cart.items.find((item: CartProduct) => item.productId === payload.productId)
  }
  const removeCartProduct = async ({ productId, isPromotionalPricing = false }: { productId: string, isPromotionalPricing?: boolean }) => {
    const product = items.value.find(product => product.productId === productId)
    if (!product) {
      throw new Error(`product with product id ${productId} should exist in cart items for removing`)
    }
    const result = await $cartService.removeProduct({ productId, isPromotionalPricing })
    updateCartState(result)
    pushRemoveFromCart(gtm, product, localeStore.currency)
  }
  const updateCartProductQuantity = async ({ product, quantity, isIncrement, isPromotionalPricing }: AddProductPayload) => {
    const updateResult = await $cartService.updateQuantity({ product, quantity, isIncrement, isPromotionalPricing })
    if (!updateResult) {
      return
    }
    updateCartState(updateResult)
  }

  const addCartProductById = async ({ culture, productId, quantity = 1 }: {culture: string, productId: string, quantity: number}) => {
    const query = addCommonQueryParam()
    query.id = productId
    const response = await api<QueryProductsAPIResponse>(
      'products/',
      {
        query,
        culture
      }
    )
    const [first] = response.products
    assertIsDefined(first, `product not found ${productId}`)
    await addProductToCart({
      product: first,
      quantity,
      source: AddToCartSource.QUICK_ADD_BY_ID
    })
  }

  // Demo ordering is blocked for the active audience; every add surface shows the same
  // error notification. `force` keeps it visible when another notification is already open.
  const notifyDemoOrderNotAllowed = (error: unknown, { force = false }: { force?: boolean } = {}) => {
    if (!hasApiErrorCode(error, 'DemoOrderNotAllowedError')) {
      return false
    }
    useNotificationsStore().showNotification({
      notification: {
        type: NotificationType.Error,
        props: {
          title: 'header.demoOrderingNotAvailable',
          message: displayMessage(error)
        }
      },
      force
    })
    return true
  }

  const addProductToCart = async ({ product, quantity = 1, source }: AddProductPayload): Promise<AddProductToCartResult> => {
    const notificationStore = useNotificationsStore()
    if (quantity < 1) {
      return
    }
    let addResult: CartUpdateResult | undefined
    try {
      addResult = await $cartService.addProduct({ product, quantity })
    } catch (error: unknown) {
      if (notifyDemoOrderNotAllowed(error)) {
        return 'unsuccesful'
      }
      throw (error)
    }

    if (!addResult) {
      return 'unsuccesful'
    }
    updateCartState(addResult)

    const route = router?.currentRoute

      const category = getCategoryNameForCartRoute(route.value, slug => navigationStore.categoryBySlug(slug))
    pushAddToCart(gtm, product, localeStore.currency, quantity, category, source)
    // TODO: Clean up the B/E to be consistent with product.id
    // When adding to cart directly B/E returns product with id property
    // When adding from wishlist it returns product with productId property
    const item = items.value.find(cartItem => cartItem.productId === (product.id ?? product.productId))
    notificationStore.showNotification({
      notification: {
        type: NotificationType.CartItemAdded,
        props: {
          product: item
        }
      }
    })
  }

  const updateLoyaltyPoints = async (amount: string) => {
    const query = addCommonQueryParam()
    const { cart: cartData, cartSummaryOverview, apiError: { errors } } = await api<CartApiResponse>('cart/loyalty-points', { body: { points: amount }, query, method: 'PATCH' })
    updateCartState({ cart: cartData, cartSummaryOverview, errors })
    if (cart.value.demonstrator?.demoId === 0) {
      cart.value.demonstrator = null
    }
  }

  const loadCart = async () => {
    const result = await $cartService.fetch()
    updateCartState(result)
    // TODO: handle null demonstrator by demoId, forbid `null` type
    if (cart.value.demonstrator?.demoId === 0) {
      cart.value.demonstrator = null
    }
  }
  // Server only: the cart request in flight, settled either way, for the gate in useCartStore() below.
  let serverFetch: Promise<void> | undefined
  const fetchCart = () => {
    const request = loadCart()
    if (isServer) {
      const settled = request.then(() => {}, () => {})
      serverFetch = settled
      settled.then(() => {
        if (serverFetch === settled) serverFetch = undefined
      })
    }
    return request
  }
  const pendingServerFetch = () => serverFetch
  const replaceCart = async () => {
    const query = addCommonQueryParam()
    const cartUrl = `${URL_CART.CART}/${cart.value.id}`
    const cartNew = await api<Cart>(cartUrl, { query, method: 'PATCH' })
    cart.value = cartNew
  }
  const mergeCarts = async (cartNew: Cart) => {
    const cartUrl: string = `${URL_CART.CART}/${cartNew.id}`
    const query = addCommonQueryParam()
    const body = { isMerge: 'true' }

    const response = await api<({cart: Cart, cartSummaryOverview: CartSummary })>(cartUrl, { body, query, method: 'PATCH' })
    const mergedCart = response.cart
    const mergedQty = mergedCart.totalProducts - cart.value.totalProducts

    cart.value = response.cart
    summary.value = response.cartSummaryOverview
    if (!cart.value.demonstrator?.demoId) {
      cart.value.demonstrator = null
    }
    if (mergedQty > 0) {
      const notificationStore = useNotificationsStore()
      notificationStore.showNotification({
        notification: {
          type: NotificationType.CartMerged,
          props: {
            productQty: mergedQty
          }
        }
      })
    }
  }
  const removeDemoId = async () => {
    const url = `${URL_CART.CART}${URL_CART.REMOVE_DEMOID}`
    const query = addCommonQueryParam()

    try {
      await api(url, { query, method: 'DELETE' })
      await fetchCart()
    } catch {
      // deliberately swallow
    }
  }
  const updateDemoId = async (demoId: number) => {
    const culture = localeStore.culture
    const query = addCommonQueryParam()
    try {
      if (getIsValidDemoForCulture(culture, demoId)) {
        const url = `${URL_CART.CART}${URL_CART.DEMOID}${demoId}`
        await api(url, { query, method: 'PUT' })
        await fetchCart()
      }
    } catch {
      // deliberately swallow
    }
  }
  const updateDbwsId = async (demoId: number) => {
    const culture = localeStore.culture
    const query = addCommonQueryParam()
    try {
      if (getIsValidDemoForCulture(culture, demoId)) {
        const url = `${URL_CART.CART}${URL_CART.DEMOID}${demoId}?isFromDBWS=true`
        await api(url, { query, method: 'PUT' })
        await fetchCart()
      }
    } catch {
      // deliberately swallow
    }
  }
  const addProject = async (itemsNew: string) => {
    const url = `${URL_CART.CART}${URL_CART.ADD_PROJECT}?items=${itemsNew}`
    const query = addCommonQueryParam()
    const { cart, cartSummaryOverview } = await api<{ cart: Cart, cartSummaryOverview: CartSummary }>(url, { query, method: 'POST' })
    updateCartState({ cart, cartSummaryOverview })
  }

  const addCouponCodes = async (couponCodes: string[]) => {
    const query = addCommonQueryParam()
    try {
      const url = `${URL_CART.CART}${URL_CART.COUPONCODES}`
      await api(url, { body: couponCodes, query, method: 'PUT' })
    } finally {
      await fetchCart()
    }
  }
  const removeCouponCode = async (couponCode: string) => {
    try {
      await $cartService.removeCouponCode(couponCode)
      await fetchCart()
    } catch {
      // deliberately swallow
    }
  }
  const initSharedCart = async () => {
    const query = addCommonQueryParam()
    const { cart, cartSummaryOverview, apiError: { errors } } = await api<CartApiResponse>('/cart/cart-sharing', { query, method: 'POST' })
    updateCartState({ cart, cartSummaryOverview, errors })
    await router.replace({ query: { ...router.currentRoute.value.query, sharedCartId: undefined } })
  }
  const exitSharedCart = async () => {
    const isSharedMode = isSharedCartMode.value
    persistentStore.updateValue('sharedCartId', null)
    if (isSharedMode) {
      await router.replace('/')
    }
  }

  const items = computed(() => cart.value.items)
  const subTotal = computed(() => summary.value.subTotal ?? 0)
  const productQtyInCart = (productId: string) => items.value.reduce((tn, product) =>
    product.productId.toString() === productId ? tn + product.quantity : tn
  , 0)
  const availableQtyForPurchase = (productId: string, qtyLimit?: number, exclusiveTo? : string []) => {
    if (exclusiveTo?.includes(ExclusiveTo.SUBSCRIPTION)) { return 1 }

    const productLimit = qtyLimit || DEFAULT_MAX_LIMIT
    const numberInCart = productQtyInCart(productId)
    return Math.max(productLimit - numberInCart, 0)
  }
  const addAllWishlistItemsToCart = async (wishlist: Wishlist) => {
    const notificationStore = useNotificationsStore()
    const url = `${URL_CART.CART}${URL_CART.WISHLIST}${wishlist.id}`
    const query = addCommonQueryParam()
    try {
      const { itemsNotAdded }: { itemsNotAdded: CartProduct[] } = await api(url, { query, method: 'POST' })
      const qtyAdded = (wishlist.items?.length ?? wishlist.totalQuantity) - (itemsNotAdded?.length ?? 0)
      if (qtyAdded > 0) {
        await fetchCart()
        notificationStore.showNotification({
          notification: {
            type: NotificationType.CartItemAdded,
            props: {}
          }
        })
      }
      return itemsNotAdded
    } catch (error: unknown) {
      if (notifyDemoOrderNotAllowed(error, { force: true })) {
        return 'unsuccesful'
      }
      return []
      // deliberately swallow
    }
  }

  const removeNonpurchasableItems = async () => {
    const query = addCommonQueryParam()

    await api(URL_CART.CART + '/remove-nonpurchasable-items', { query, method: 'DELETE' })
    await fetchCart()
  }

  const groupShippingCode = computed(() => cart.value.groupShippingCode)
  const hasDbwsDemo = computed(() => cart.value.isFromDbws)
  const appliedCoupons = computed(() => cart.value.couponCodes ?? [])
  const hasCouponCodes = computed(() => appliedCoupons.value.length > 0)
  const itemsExceededLimit = computed(() => items.value.filter(i => i.quantity > i.qtyLimit))
  const backOrderedItems = computed(() => items.value.filter(i => !i.canBePurchased))
  const purchasableItems = computed(() => items.value.filter(i => i.canBePurchased &&
  ('itemType' in i && i.itemType === ItemType.NORMAL) && !i.isPromotionalPricing))
  const promotionalItems = computed(() => items.value.filter(i => i.isPromotionalPricing))
  const submitRoute = computed(() => '/checkout')
  // The summary carries a bonus campaign effect, so Checkout goes through the bonus step first (AB#63452).
  const bonusCampaignActive = computed(() => !!summary.value.bonusItemSelection)
  // Where a guest who clicked Checkout lands after signing in: back on the cart for the bonus step, or checkout.
  const checkoutAfterSignInPath = computed(() => bonusCampaignActive.value ? BONUS_RETURN_PATH : submitRoute.value)
  const readyForCheckout = computed(() => items.value.length > 0 && summary.value.apiError.errors?.length === 0 && !items.value.some(i =>
    i.quantity > i.qtyLimit) && errors.value.length === 0)
  const demonstrator = computed(() => (cart.value.demonstrator?.demoId ?? 0) > 0 ? cart.value.demonstrator : null)
  const demonstratorName = computed(() => demonstrator.value ? `${demonstrator.value.firstName} ${demonstrator.value.lastName}` : '')
  const limitedAccessPromotions = computed(() => summary.value.promotionSummary?.filter(p => p?.limitedAccessPromotionSummary)?.map(p => p.limitedAccessPromotionSummary) ?? [])

  const cartQty = computed(() => cart.value.totalProducts - (cart.value.items.filter(i => i.itemType === ItemType.KIT).length))
  const hasRealItems = computed(() => cartQty.value > 1)

  const isSharedCartMode = computed(() => {
    try {
      return !!persistentStore.getPersistentValue('sharedCartId')
    } catch (e) {
      $appInsights.trackException({
        exception: toError(e),
        properties: {
          message: 'Error getting sharedCartId persistent value',
          funcType: typeof persistentStore.getPersistentValue,
          storeType: typeof persistentStore,
          store: persistentStore
        }
      })
      return false
    }
  })

  return {
    cart,
    items,
    errors,
    summary,
    removeNonpurchasableItems,
    quickAddItem,
    addProductToCart,
    removeDemoId,
    updateDemoId,
    addCouponCodes,
    removeCouponCode,
    updateDbwsId,
    addProject,
    mergeCarts,
    replaceCart,
    fetchCart,
    pendingServerFetch,
    initSharedCart,
    exitSharedCart,
    updateLoyaltyPoints,
    removeCartProduct,
    updateCartProductQuantity,
    addAllWishlistItemsToCart,
    itemsExceededLimit,
    subTotal,
    limitedAccessPromotions,
    availableQtyForPurchase,
    productQtyInCart,
    hasDbwsDemo,
    appliedCoupons,
    hasCouponCodes,
    backOrderedItems,
    addCartProductById,
    cartQty,
    hasRealItems,
    purchasableItems,
    promotionalItems,
    submitRoute,
    bonusCampaignActive,
    checkoutAfterSignInPath,
    readyForCheckout,
    demonstrator,
    demonstratorName,
    isSharedCartMode,
    groupShippingCode
  }
})

// Called through this name, not as onServerPrefetch(): Nuxt's browser build rewrites a direct call into a stub that drops
// the callback. In the browser that makes no difference (the stub and Vue's hook both only mark the component as a
// useId() boundary), but vitest runs that build, and through this name cart.server.spec.ts and base-cart.spec.ts render
// the real gate. They also fail if a Vue or Nuxt upgrade changes this rewrite or the scope check below.
const registerServerPrefetch = onServerPrefetch

/**
 * The cart store, with a server-render gate.
 *
 * On the server, a component that calls useCartStore() in its own setup renders its subtree only once the cart request
 * in flight at that point has settled (a server-prefetch hook), so it renders the fetched cart. BaseLayout does not wait
 * for the cart itself: its first child, BaseLayoutCart, starts the request, and the page's own requests go out alongside
 * it.
 * - Vue runs the hook once the components set up in the same pass are, so the components beside or below BaseLayoutCart
 *   find the request, whatever their order. A component above it does not: its children, BaseLayoutCart among them,
 *   are set up only once its own hook has settled. A page that renders <Layout> itself is such a component and renders
 *   the cart as it then is, empty on a first render; it reads the cart only below a component that calls the store
 *   (the cart page does it through BaseCart).
 * - Every such call gates, also one that only uses actions: that component's subtree then renders after the cart too,
 *   which matters only if the subtree starts requests of its own.
 * - Any call made while the component's setup runs counts, also one through a composable (useSAuth() calls the store,
 *   so every component that uses it is gated too); not one from another store's setup (the agent store's, created by
 *   this store) or from a render: the browser creates stores elsewhere, and both sides must gate the same components,
 *   or the hydrated useId() ids would differ from the server's.
 * cart.server.spec.ts renders the gate on the server; cart.spec.ts checks the ids in the browser.
 */
export const useCartStore = Object.assign((...args: Parameters<typeof useCartStoreDefinition>) => {
  const store = useCartStoreDefinition(...args)
  // instance.scope is Vue internal: the component's own effect scope, active only while its setup runs.
  const instance = getCurrentInstance() as (ComponentInternalInstance & { scope: EffectScope }) | null
  if (instance && getCurrentScope() === instance.scope) {
    // Read when Vue runs the hook, after the setup.
    registerServerPrefetch(() => store.pendingServerFetch())
  }
  return store
}, { $id: useCartStoreDefinition.$id }) as typeof useCartStoreDefinition
